import {DatePicker, Input, InputNumber, Select, Switch} from 'antd';
import moment from 'moment';
import styles from './FlowBuilder.module.scss';
import {useMemo, useState, useRef, useEffect} from 'react';
import {walletCurrencies} from 'utils/mock';

const HEX24 = /^[a-f\d]{24}$/i;

// Currency-named STRING filters (baseCurrencyCode, currency, assetCurrency,
// payoutCurrency, transactionCurrency…) render as a currency picker.
const CURRENCY_OPTIONS = walletCurrencies.map(c => ({
  value: c.key,
  label: `${c.key} — ${c.name}`,
}));
const isCurrencyKey = key => /currency/i.test(key || '');

// Turn enum / camelCase keys into readable words:
// "searchTerm" → "Search term", "DATE_RANGE" → "Date range",
// "investmentCategory" → "Investment category".
const humanize = value => {
  const spaced = String(value || '')
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
  if (!spaced) return '';
  const lower = spaced.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

// Plain-English labels + hints for the common filter keys, so admins don't
// read engineer-speak. Keyed by the lowercased filter key. Anything not here
// falls back to humanized label + the backend-provided description.
const FRIENDLY_COPY = {
  limit: {
    label: 'Number of results',
    hint: 'How many records to pull (defaults to 5).',
  },
  search: {
    label: 'Search',
    hint: 'Type a keyword to match — leave blank to show everything.',
  },
  searchterm: {
    label: 'Search',
    hint: 'Type a keyword (name, reference, amount…) to narrow results.',
  },
  status: {label: 'Status', hint: 'Only show records in this state.'},
  daterange: {
    label: 'Date range',
    hint: 'Only include records created between these two dates.',
  },
  type: {label: 'Type', hint: 'Filter by the kind of record.'},
  category: {label: 'Category', hint: 'Limit to one or more categories.'},
  currency: {label: 'Currency', hint: 'Only show records in this currency.'},
  basecurrencycode: {
    label: 'Base currency',
    hint: 'Only show records priced in this currency.',
  },
  assetcurrency: {
    label: 'Asset currency',
    hint: 'Only show records whose asset is in this currency.',
  },
  payoutcurrency: {
    label: 'Payout currency',
    hint: 'Only show records paid out in this currency.',
  },
  transactioncurrency: {
    label: 'Transaction currency',
    hint: 'Only show records transacted in this currency.',
  },
  activeonly: {
    label: 'Active only',
    hint: 'Show only currently-active records.',
  },
  unreadonly: {
    label: 'Unread only',
    hint: 'Show only items the user hasn’t read yet.',
  },
  companyid: {
    label: 'Company',
    hint: 'Limit to a specific company (paste its id).',
  },
  instrumentid: {
    label: 'Instrument',
    hint: 'Limit to a specific instrument (paste its id).',
  },
  ticker: {label: 'Ticker', hint: 'Filter by the trading symbol, e.g. AAPL.'},
  name: {label: 'Name', hint: 'Match records by name.'},
  language: {label: 'Language', hint: 'Only show records in this language.'},
  tradeable: {
    label: 'Tradeable',
    hint: 'Only show items the user can actually trade.',
  },
};

const copyFor = filter => {
  const friendly = FRIENDLY_COPY[(filter.key || '').toLowerCase()];
  return {
    label: friendly?.label || humanize(filter.key),
    // Prefer a plain-English hint; otherwise show the backend description.
    hint: friendly?.hint || filter.description || '',
  };
};

// Friendly short label for the type chip (admins don't think in "INT").
const TYPE_LABEL = {
  STRING: 'Text',
  BOOLEAN: 'Yes / No',
  INT: 'Number',
  FLOAT: 'Number',
  DATE: 'Date',
  DATE_RANGE: 'Date range',
  OBJECT_ID: 'ID',
  STRING_LIST: 'List',
  ENUM: 'Choice',
  ENUM_LIST: 'Choices',
  NUMBER_RANGE: 'Range',
};

const typeLabel = type => TYPE_LABEL[type] || humanize(type);

const isEmpty = v => {
  if (v === undefined || v === null || v === '') return true;
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') {
    return Object.values(v).every(
      x => x === undefined || x === null || x === '',
    );
  }
  return false;
};

const enumOptions = values =>
  (values || []).map(v => ({value: v, label: humanize(v)}));

/**
 * Typed filter form rendered from a resolver's `supportedFilters`.
 *
 * Controlled by <Form.Item name={[name, 'payload']}> — antd injects
 * `value` (the payload object) + `onChange`. Unknown keys (legacy configs
 * whose filters are no longer advertised) are preserved untouched and
 * re-emitted on every change. Empty values are stripped so the saved
 * payload only carries what the admin actually set.
 */
export const FilterForm = ({descriptor, value, onChange}) => {
  const payload = value && typeof value === 'object' ? value : {};
  const filters = descriptor?.supportedFilters || [];
  const filterByKey = useMemo(
    () => Object.fromEntries(filters.map(f => [f.key, f])),
    [filters],
  );

  // Keys present in the payload but no longer advertised by the resolver.
  const knownKeys = new Set(filters.map(f => f.key));
  const unknownKeys = Object.keys(payload).filter(k => !knownKeys.has(k));

  // Which filters are shown as inputs (opt-in). Seeded from the keys the
  // payload already has values for, so edit mode hydrates correctly.
  const seedSelected = () =>
    filters.filter(f => !isEmpty(payload[f.key])).map(f => f.key);
  const [selectedKeys, setSelectedKeys] = useState(seedSelected);

  // When the resolver (filter set) changes, reset shown filters to whatever
  // the payload already carries values for.
  const resolverKey = descriptor?.resolver;
  const lastResolver = useRef(resolverKey);
  useEffect(() => {
    if (lastResolver.current !== resolverKey) {
      lastResolver.current = resolverKey;
      setSelectedKeys(seedSelected());
    }
    // eslint-disable-next-line
  }, [resolverKey]);

  const setKey = (key, next) => {
    const updated = {...payload};
    if (isEmpty(next)) {
      delete updated[key];
    } else {
      updated[key] = next;
    }
    onChange(updated);
  };

  const handlePickChange = nextKeys => {
    // Deselecting a filter clears any value it held.
    const removed = selectedKeys.filter(k => !nextKeys.includes(k));
    if (removed.length) {
      const updated = {...payload};
      removed.forEach(k => delete updated[k]);
      onChange(updated);
    }
    setSelectedKeys(nextKeys);
  };

  if (!filters.length) {
    return (
      <div className={styles.filterFormEmpty}>
        This resolver takes no filters — it returns its full result set.
      </div>
    );
  }

  const pickerOptions = filters.map(f => ({
    value: f.key,
    label: copyFor(f).label,
  }));

  const renderInput = filter => {
    const {key, type, enumValues} = filter;
    const current = payload[key];

    switch (type) {
      case 'BOOLEAN':
        return (
          <div className={styles.switchWrap}>
            <Switch
              checked={!!current}
              onChange={v => setKey(key, v || undefined)}
            />
          </div>
        );

      case 'INT':
      case 'FLOAT':
        return (
          <InputNumber
            style={{width: '100%'}}
            precision={type === 'INT' ? 0 : undefined}
            value={current ?? undefined}
            placeholder={
              key === 'limit' && descriptor?.defaultLimit != null
                ? String(descriptor.defaultLimit)
                : undefined
            }
            onChange={v => setKey(key, typeof v === 'number' ? v : undefined)}
          />
        );

      case 'DATE':
        return (
          <DatePicker
            style={{width: '100%'}}
            value={current ? moment(current) : null}
            onChange={d => setKey(key, d ? d.toISOString() : undefined)}
          />
        );

      case 'DATE_RANGE': {
        const range = current || {};
        const setPart = part => d =>
          setKey(key, {...range, [part]: d ? d.toISOString() : undefined});
        return (
          <div className={styles.filterRangeRow}>
            <DatePicker
              style={{width: '100%'}}
              placeholder="From"
              value={range.from ? moment(range.from) : null}
              onChange={setPart('from')}
            />
            <DatePicker
              style={{width: '100%'}}
              placeholder="To"
              value={range.to ? moment(range.to) : null}
              onChange={setPart('to')}
            />
          </div>
        );
      }

      case 'NUMBER_RANGE': {
        const range = current || {};
        const setPart = part => v =>
          setKey(key, {
            ...range,
            [part]: typeof v === 'number' ? v : undefined,
          });
        return (
          <div className={styles.filterRangeRow}>
            <InputNumber
              style={{width: '100%'}}
              placeholder="Min"
              value={range.min ?? undefined}
              onChange={setPart('min')}
            />
            <InputNumber
              style={{width: '100%'}}
              placeholder="Max"
              value={range.max ?? undefined}
              onChange={setPart('max')}
            />
          </div>
        );
      }

      case 'OBJECT_ID': {
        const invalid = !!current && !HEX24.test(current);
        return (
          <Input
            allowClear
            status={invalid ? 'error' : undefined}
            placeholder="Paste the record ID"
            value={current || ''}
            onChange={e => setKey(key, e.target.value.trim() || undefined)}
          />
        );
      }

      case 'STRING_LIST':
        return (
          <Select
            mode="tags"
            style={{width: '100%'}}
            tokenSeparators={[',']}
            placeholder="Type a value and press Enter"
            value={current || []}
            onChange={v => setKey(key, v)}
          />
        );

      case 'ENUM':
        return (
          <Select
            allowClear
            showSearch
            style={{width: '100%'}}
            placeholder="Select"
            optionFilterProp="label"
            value={current ?? undefined}
            options={enumOptions(enumValues)}
            onChange={v => setKey(key, v ?? undefined)}
          />
        );

      case 'ENUM_LIST':
        return (
          <Select
            mode="multiple"
            allowClear
            style={{width: '100%'}}
            placeholder="Select one or more"
            optionFilterProp="label"
            value={current || []}
            options={enumOptions(enumValues)}
            onChange={v => setKey(key, v)}
          />
        );

      case 'STRING':
      default:
        if (isCurrencyKey(key)) {
          return (
            <Select
              allowClear
              showSearch
              style={{width: '100%'}}
              placeholder="Select currency"
              optionFilterProp="label"
              value={current ?? undefined}
              options={CURRENCY_OPTIONS}
              onChange={v => setKey(key, v ?? undefined)}
            />
          );
        }
        return (
          <Input
            allowClear
            value={current || ''}
            onChange={e => setKey(key, e.target.value || undefined)}
          />
        );
    }
  };

  const activeFilters = selectedKeys.map(k => filterByKey[k]).filter(Boolean);

  return (
    <div className={styles.filterForm}>
      <div className={styles.filterPicker}>
        <Select
          mode="multiple"
          allowClear
          style={{width: '100%'}}
          placeholder="Add a filter…"
          optionFilterProp="label"
          options={pickerOptions}
          value={selectedKeys}
          onChange={handlePickChange}
          getPopupContainer={trigger => trigger.parentNode}
        />
        <span className={styles.filterPickerHint}>
          Choose which dimensions to filter by — only the ones you add show up
          below.
        </span>
      </div>

      {activeFilters.length ? (
        <div className={styles.filterFormGrid}>
          {activeFilters.map(filter => {
            const copy = copyFor(filter);
            return (
              <div className={styles.filterField} key={filter.key}>
                <label className={styles.filterFieldLabel}>
                  {copy.label}
                  <span className={styles.filterFieldType}>
                    {typeLabel(filter.type)}
                  </span>
                </label>
                {renderInput(filter)}
                {copy.hint ? (
                  <span className={styles.filterFieldHint}>{copy.hint}</span>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {unknownKeys.length ? (
        <div className={styles.filterUnknown}>
          Preserved legacy keys (not editable here):{' '}
          <strong>{unknownKeys.join(', ')}</strong>
        </div>
      ) : null}
    </div>
  );
};
